
import React from 'react';
import { PlateConfig, PlateType, FlagType, Dimensions, TextEffect, PlateDimensions, LipTextConfig, LipTextItem, LipImageItem } from '../types';

interface PlateViewProps {
  config: PlateConfig;
  dimensions?: Dimensions;
  id?: string;
  className?: string;
  style?: React.CSSProperties;
  forceTransparent?: boolean;
  unit?: string;
  // Crop lines visualization
  showCropLines?: boolean;
  cropLinesOffset?: number;
  // Floating positioning props for mobile
  position?: 'fixed' | 'relative';
  top?: string;
  left?: string;
  right?: string;
  bottom?: string;
  transform?: string;
  transformOrigin?: string;
  transition?: string;
  touchAction?: string;
  zIndex?: number;
  pointerEvents?: string;
}

export const PlateView: React.FC<PlateViewProps> = ({
  config,
  dimensions,
  id,
  className = '',
  style: propStyle,
  forceTransparent = false,
  unit = 'px',
  // Crop lines visualization
  showCropLines = false,
  cropLinesOffset = 5,
  // Floating positioning props for mobile
  position = 'relative',
  top,
  left,
  right,
  bottom,
  transform,
  transformOrigin,
  transition,
  touchAction,
  zIndex,
  pointerEvents
}) => {
  const dims = dimensions || PlateDimensions[config.plateSize] || PlateDimensions['REGULAR'];
  const isFront = config.type === PlateType.FRONT;
  
  const plateColor = (isFront || forceTransparent) ? '#ffffff' : '#facc15';
  
  const shapePath = dims.pathData;
  const isShaped = !!shapePath;
  
  const plateCornerRadius = 10; 
  const flagWidth = 44; 

  const hasSlogan = !!config.slogan && config.slogan.trim().length > 0;
  
  const renderBadge = () => {
    // Render badge if enabled
    if (!config.badge.enabled) return null;
    // Removed shape check to allow badges on all plate types
    // if (isShaped) return null;  
    
    const badgeColors: Record<string, string> = {
      BLUE: '#003399',
      GREEN: '#17C956',
      BLACK: '#1a1a1a',
      NONE: 'transparent'
    };
    
    // Determine flag type logic
    const isEV = config.isEV || config.badge.type === FlagType.EV;
    const finalBackgroundColor = isEV ? 'GREEN' : config.badge.backgroundColor;
    const key = finalBackgroundColor?.toUpperCase();
    const badgeColor = badgeColors[key] || badgeColors[finalBackgroundColor] || 'transparent';
    
    // Text Color Override or Default
    const defaultTextColor = badgeColor === 'transparent' ? '#000000' : '#FFFFFF';
    const textColor = config.badge.identifierTextColor || defaultTextColor;
    
    // Choose Graphic
    // Unified logic: prefer the data URL (customSvgUrl) if available, otherwise fallback to path
    const flagName = config.badge.type; 
    // If we have a custom URL (loaded via App.tsx or uploaded), use it. 
    // Otherwise construct path. Note: App.tsx now preloads standard flags into customSvgUrl too.
    const imgSrc = config.badge.customSvgUrl || `/flags/${flagName}.svg`;

    const badgeHeight = dims.height;
    const badgeWidth = flagWidth;
    const inset = 2; 
    const innerRadius = config.badge.borderRadius || 14; 
    const rotationDegrees = config.badge.isVertical ? -90 : 0;
    const scale = config.badge.graphicScale || 1;
    const vOffset = config.badge.graphicVOffset || 0;
    const pxPerMm = 3.779527559;
    const vOffsetPx = vOffset * (unit === 'mm' ? pxPerMm : 1);

    // Calculate available width/height for the image content
    const availableWidth = badgeWidth - (inset * 2) - 8; // 8 = inner padding * 2
    const availableHeight = badgeHeight - (inset * 2) - 8;

    const imgSize = availableWidth;

    const FlagGraphic = (
        <div
            style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: `translate(-50%, -50%) translateY(${vOffsetPx}px)`
            }}
        >
            <img 
                src={imgSrc} 
                alt={flagName} 
                style={{ 
                    display: 'block',
                    transformOrigin: 'center center',
                    transform: `rotate(${rotationDegrees}deg) scale(${scale})`,
                    width: config.badge.isVertical ? `${imgSize}${unit}` : `${availableWidth}${unit}`, 
                    height: config.badge.isVertical ? `${imgSize}${unit}` : `${availableHeight}${unit}`, 
                    maxWidth: 'none',
                    maxHeight: 'none',
                    objectFit: 'contain' 
                }} 
            />
        </div>
    ); 

    return (
      <div 
        className="absolute left-0 top-0 z-30 flex items-center justify-center"
        style={{ 
            width: `${badgeWidth}${unit}`, 
            height: `${badgeHeight}${unit}`,
            padding: `${inset}${unit}`
        }}
      >
         <div
            className="w-full h-full relative"
            style={{
                backgroundColor: badgeColor,
                borderRadius: `${innerRadius}${unit}`,
                overflow: 'hidden'
            }}
         >
             {config.showFlagGraphic && FlagGraphic}

             {config.badge.showIdentifier && (
                 <div 
                    className="font-bold font-mono text-center leading-none uppercase"
                    style={{ 
                        position: 'absolute',
                        bottom: `${2}${unit}`,
                        left: '0',
                        right: '0',
                        color: textColor,
                        fontSize: `${config.badge.identifierTextSize || 14}${unit}`,
                        width: '100%',
                        zIndex: 50,
                        transform: `translateY(${-(config.badge.identifierTextVOffset || 0) * (unit === 'mm' ? pxPerMm : 1)}px)`,
                        textShadow: '0px 0px 2px rgba(0,0,0,0.5)'
                    }}
                 >
                     {config.badge.identifierText}
                 </div>
             )}
         </div>
      </div>
    );
  };

  const renderSVGContent = () => {
    const borderLeft = config.flagLayout ? (flagWidth + config.borderMargin) : config.borderMargin;
    const borderTop = config.borderMargin;
    const borderWidth = dims.width - borderLeft - config.borderMargin;
    const borderHeight = dims.height - (config.borderMargin * 2);
    const defaultBorderRadius = Math.max(0, plateCornerRadius - config.borderMargin);
    const configuredBorderRadius = config.borderCornerRadius ?? config.cornerRadius;
    const borderInnerRadius = Math.max(0, Math.min(plateCornerRadius, configuredBorderRadius ?? defaultBorderRadius));

    const getFooterY = (location: string, isBorderEnabled: boolean, fontSize: number, verticalOffset: number = 0) => {
        let baseY = dims.height - 3;

        if (location === 'CUSTOM') {
            return baseY - (verticalOffset || 0);
        }

        if (isBorderEnabled) {
            if (location === 'ON_BORDER') {
                const lineCenter = dims.height - config.borderMargin - (config.borderThickness / 2);
                baseY = lineCenter + (fontSize * 0.35);
                // Clamp to prevent text + stroke from exceeding plate boundary
                // Text descender is ~20% of fontSize, stroke adds ~2.5mm each side
                const maxY = dims.height - 1;
                baseY = Math.min(baseY, maxY);
            } else if (location === 'ABOVE_BORDER') {
                baseY = dims.height - config.borderMargin - config.borderThickness - 1;
            } else if (location === 'BELOW_BORDER') {
                baseY = dims.height - 1;
            }
        }
        return baseY - (verticalOffset || 0);
    };

    let showDealer = config.dealerText.show;
    let showBSAU = config.bsauText.show;

    if (config.isShowPlate) {
        if (config.showPlateOverrides.removeDealer) showDealer = false;
        if (config.showPlateOverrides.removeBSAU) showBSAU = false;
    }
    
    const showSlogan = hasSlogan; 
    const showDealerCenter = showDealer && !config.supplierInCorner && !showSlogan;
    const showDealerCorner = showDealer && config.supplierInCorner;
    const footerCenterX = (config.flagLayout || config.badge.enabled) ? (dims.width / 2) + 22 : (dims.width / 2);
    
    const rawReg = config.registration || 'REG';
    const lines = rawReg.split('\n');
    
    const mainFontSize = config.mainFontSize;
    const textAreaLeft = config.flagLayout ? flagWidth : 0;
    const textX = config.mainTextAlignment === 'left' ? textAreaLeft + config.borderMargin + 10 : 
                  config.mainTextAlignment === 'right' ? dims.width - config.borderMargin - 10 :
                  textAreaLeft + (config.flagLayout ? (dims.width - flagWidth) : dims.width) / 2;
    
    const textAnchor = config.mainTextAlignment === 'left' ? 'start' : config.mainTextAlignment === 'right' ? 'end' : 'middle';

    // For Charles Wright Radial, if offset is 0, use -6 internally to center it (user still sees 0)
    const effectiveVerticalOffset = (config.mainFontFamily === 'Charles Wright Radial' && config.mainTextVerticalOffset === 0) 
        ? -6 
        : config.mainTextVerticalOffset;
    const centerY = (dims.height * 0.5) - effectiveVerticalOffset;
    
    // Font-specific baseline offsets for proper centering by visual glyph bounds
    // These account for different font metrics (ascent/descent ratios)
    // Values are calibrated to center the visual glyphs, not the em box
    const fontCenteringOffsets: Record<string, number> = {
        'Charles Wright Radial': -7,
        'Charles Moto Radial': -7,
        'Charles Wright': -5,
        'Charles Moto': -5,
        'Mandatory': -10,
        'Metro Radial': -7,
        'Euro': -4,
        'Orbitron': -2,
        'Arial': 0,
        'DIN Next': -3,
        'Retro': -6,
        'RetroSkinny': -6
    };
    const fontSpecificOffset = fontCenteringOffsets[config.mainFontFamily] || 0;
    
    // Base offset (0.40 works well for most fonts)
    const baselineOffset = mainFontSize * 0.40;
    const textBaselineY = centerY + baselineOffset + fontSpecificOffset;
    const lineHeight = mainFontSize * (config.mainTextLineHeight || 0.9);

    const renderMainTextLine = (text: string, lineIndex: number) => {
        const lineOffset = (lineIndex - (lines.length - 1) / 2) * lineHeight;

        // Fonts that need 25% narrower spaces
        const narrowSpaceFonts = ['Charles Wright', 'Charles Moto', 'Charles Moto Radial', 'Metro'];
        const needsNarrowSpace = narrowSpaceFonts.includes(config.mainFontFamily);

        if (config.isShowPlate && config.showPlateOverrides.reduceSpace) {
            const parts = text.split(/( )/g);
            return (
                <tspan key={lineIndex} x={textX} dy={lineIndex === 0 ? lineOffset : lineHeight}>
                    {parts.map((part, i) => {
                        if (part === ' ') {
                            return <tspan key={i} fontSize={`${mainFontSize * 0.25}px`} letterSpacing="0px">{' '}</tspan>;
                        }
                        return <tspan key={i}>{part}</tspan>;
                    })}
                </tspan>
            );
        }

        // For fonts with wide spaces, reduce space width by 25%
        if (needsNarrowSpace && text.includes(' ')) {
            const parts = text.split(/( )/g);
            return (
                <tspan key={lineIndex} x={textX} dy={lineIndex === 0 ? lineOffset : lineHeight}>
                    {parts.map((part, i) => {
                        if (part === ' ') {
                            // Render space at 75% width (25% narrower)
                            return <tspan key={i} style={{ fontSize: `${mainFontSize * 0.75}px` }}>{' '}</tspan>;
                        }
                        return <tspan key={i}>{part}</tspan>;
                    })}
                </tspan>
            );
        }

        return <tspan key={lineIndex} x={textX} dy={lineIndex === 0 ? lineOffset : lineHeight}>{text}</tspan>;
    };

    const textEffectFilter = config.textEffect === TextEffect.SHADOW_3D ? 'url(#shadow3d)' : undefined;
    const textEffectFill = config.textEffect === TextEffect.CARBON ? 'url(#carbon)' : 
                           config.textEffect === TextEffect.HONEYCOMB ? 'url(#honeycomb)' : 
                           config.textColor;
    
    const stroke = config.textEffect === TextEffect.HIGHLINE ? '#9ca3af' : undefined;
    const strokeWidth = config.textEffect === TextEffect.HIGHLINE ? 1.5 : undefined;

    const viewBoxWidth = parseFloat(dims.viewBox?.split(' ')[2] || '1');
    const scaleFactor = viewBoxWidth / dims.width; 
    const marginUnits = config.borderMargin * scaleFactor;
    const thickUnits = config.borderThickness * scaleFactor;
    const strokeWidthOuter = (marginUnits + thickUnits) * 2;
    const strokeWidthInner = marginUnits * 2;

    const clipId = `plate-clip-${id || 'default'}`;

    return (
        <svg
            width="100%"
            height="100%"
            viewBox={`0 0 ${dims.width} ${dims.height}`}
            style={{ overflow: 'visible', position: 'absolute', top: 0, left: 0, zIndex: 20 }}
        >
            <defs>
                {shapePath && (
                    <clipPath id={clipId}>
                        <path d={shapePath} transform={dims.pathTransform} />
                    </clipPath>
                )}

                <filter id="shadow3d" x="-20%" y="-20%" width="140%" height="140%">
                    <feOffset result="offOut" in="SourceGraphic" dx="2" dy="2" />
                    <feColorMatrix result="matrixOut" in="offOut" type="matrix"
                    values="0 0 0 0 0.6 0 0 0 0 0.6 0 0 0 0 0.6 0 0 0 1 0" />
                    <feBlend in="SourceGraphic" in2="matrixOut" mode="normal" />
                </filter>

                <pattern id="carbon" width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                    <rect width="2" height="4" transform="translate(0,0)" fill="#1a1a1a"></rect>
                    <rect width="2" height="4" transform="translate(2,0)" fill="#333333"></rect>
                </pattern>

                <pattern id="honeycomb" width="8" height="14" patternUnits="userSpaceOnUse">
                     <rect width="8" height="14" fill="#1a1a1a"/>
                     <path d="M4 0L8 2V6L4 8L0 6V2L4 0Z" fill="#333333" stroke="#000" strokeWidth="0.5"/>
                     <path d="M4 7L8 9V13L4 15L0 13V9L4 7Z" transform="translate(4, 7)" fill="#333333" stroke="#000" strokeWidth="0.5"/>
                </pattern>
            </defs>

            {/* BACKGROUND & BORDER */}
            {shapePath ? (
                <svg x="0" y="0" width="100%" height="100%" viewBox={dims.viewBox} preserveAspectRatio="none">
                    <g clipPath={`url(#${clipId})`}>
                        <path 
                            d={shapePath}
                            fill={plateColor}
                            stroke="none"
                            transform={dims.pathTransform}
                        />
                        {config.border && (
                            <>
                                <path 
                                    d={shapePath}
                                    fill="none"
                                    stroke={config.borderColor}
                                    strokeWidth={strokeWidthOuter}
                                    transform={dims.pathTransform}
                                />
                                <path 
                                    d={shapePath}
                                    fill="none"
                                    stroke={plateColor}
                                    strokeWidth={strokeWidthInner}
                                    transform={dims.pathTransform}
                                />
                            </>
                        )}
                    </g>
                </svg>
            ) : (
                <>
                    <rect 
                        x="0" y="0" width="100%" height="100%" rx={plateCornerRadius} 
                        fill={plateColor} 
                        stroke="none"
                    />
                    {config.border && (
                        <rect 
                            x={borderLeft} 
                            y={borderTop} 
                            width={borderWidth} 
                            height={borderHeight} 
                            rx={borderInnerRadius} 
                            fill="none" 
                            stroke={config.borderColor} 
                            strokeWidth={config.borderThickness}
                        />
                    )}
                </>
            )}

            {/* TEXT AND CONTENT */}
            <g>
                <text 
                    x={textX}
                    y={textBaselineY}
                    fill={textEffectFill}
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                    filter={textEffectFilter}
                    textAnchor={textAnchor}
                    fontSize={mainFontSize} 
                    letterSpacing={config.letterSpacing}
                    xmlSpace="preserve"
                    style={{
                        whiteSpace: 'pre', 
                        fontFamily: config.mainFontFamily,
                        fontWeight: config.mainTextBold ? 'bold' : (config.mainFontFamily === 'Orbitron' ? 'bold' : 'normal'),
                        fontStyle: config.mainTextItalic ? 'italic' : 'normal',
                        fontKerning: config.enableKerning ? 'normal' : 'none',
                        transformBox: 'fill-box',
                        transformOrigin: 'center',
                        // Forced bold using text-shadow (works even if font doesn't have bold variant)
                        // Use fill color to preserve original color
                        textShadow: config.mainTextBold && config.mainFontFamily !== 'Orbitron' ? 
                            `0.5px 0 0 ${textEffectFill}, -0.5px 0 0 ${textEffectFill}, 0 0.5px 0 ${textEffectFill}, 0 -0.5px 0 ${textEffectFill}` : 'none',
                        // Combined transform: scale for height + skew for forced italic
                        transform: config.mainTextItalic && !config.mainTextBold ? 
                            `scale(1, ${config.mainTextHeightScale || 1}) skewX(-10deg)` :
                            config.mainTextItalic && config.mainTextBold ?
                            `scale(1, ${config.mainTextHeightScale || 1}) skewX(-10deg)` :
                            `scale(1, ${config.mainTextHeightScale || 1})`
                    }}
                >
                    {lines.map((line, i) => renderMainTextLine(line, i))}
                </text>

                {showSlogan && (
                    <g>
                        {/* Knockout stroke layer - drawn first */}
                        {(config.sloganLocation || 'ON_BORDER') === 'ON_BORDER' && config.border && (
                            <text
                                x={footerCenterX}
                                y={getFooterY(config.sloganLocation || 'ON_BORDER', config.border, config.sloganConfig.fontSize, config.sloganConfig.yOffset || 0)}
                                fill="none"
                                textAnchor="middle"
                                stroke={plateColor}
                                strokeWidth={5}
                                strokeLinejoin="round"
                                fontSize={config.sloganConfig.fontSize}
                                style={{
                                    fontFamily: config.sloganConfig.fontFamily,
                                    fontWeight: config.sloganConfig.bold ? 'bold' : 'normal',
                                    fontStyle: config.sloganConfig.italic ? 'italic' : 'normal',
                                    transform: config.sloganConfig.italic ? 'skewX(-10deg)' : 'none',
                                    transformOrigin: 'center'
                                }}
                            >
                                {config.slogan}
                            </text>
                        )}
                        {/* Fill layer - drawn on top */}
                        <text
                            x={footerCenterX}
                            y={getFooterY(config.sloganLocation || 'ON_BORDER', config.border, config.sloganConfig.fontSize, config.sloganConfig.yOffset || 0)}
                            fill={config.sloganConfig.color}
                            textAnchor="middle"
                            fontSize={config.sloganConfig.fontSize}
                            stroke={config.sloganConfig.bold ? config.sloganConfig.color : 'none'}
                            strokeWidth={config.sloganConfig.bold ? 0.3 : 0}
                            style={{
                                fontFamily: config.sloganConfig.fontFamily,
                                fontWeight: config.sloganConfig.bold ? 'bold' : 'normal',
                                fontStyle: config.sloganConfig.italic ? 'italic' : 'normal',
                                transform: config.sloganConfig.italic ? 'skewX(-10deg)' : 'none',
                                transformOrigin: 'center'
                            }}
                        >
                            {config.slogan}
                        </text>
                        {/* Outline effect layer - optional extra thickening */}
                        {config.sloganConfig.outline && (
                            <text
                                x={footerCenterX}
                                y={getFooterY(config.sloganLocation || 'ON_BORDER', config.border, config.sloganConfig.fontSize, config.sloganConfig.yOffset || 0)}
                                fill="none"
                                textAnchor="middle"
                                stroke={config.sloganConfig.color}
                                strokeWidth={0.5}
                                fontSize={config.sloganConfig.fontSize}
                                style={{
                                    fontFamily: config.sloganConfig.fontFamily,
                                    fontWeight: config.sloganConfig.bold ? 'bold' : 'normal',
                                    fontStyle: config.sloganConfig.italic ? 'italic' : 'normal',
                                    transform: config.sloganConfig.italic ? 'skewX(-10deg)' : 'none',
                                    transformOrigin: 'center'
                                }}
                            >
                                {config.slogan}
                            </text>
                        )}
                    </g>
                )}

                {showDealerCenter && (
                    <g>
                        {/* Knockout stroke layer for dealer text */}
                        {config.dealerLocation === 'ON_BORDER' && config.border && !config.useDealerLogo && (
                            <text
                                x={footerCenterX}
                                y={getFooterY(config.dealerLocation, config.border, config.dealerText.fontSize, config.dealerVerticalOffset)}
                                textAnchor="middle"
                                fill="none"
                                stroke={plateColor}
                                strokeWidth={5}
                                strokeLinejoin="round"
                            >
                                <tspan
                                    fontFamily={config.dealerText.fontFamily}
                                    fontSize={config.dealerText.fontSize}
                                    fontWeight={config.dealerText.bold ? 'bold' : 'normal'}
                                    fontStyle={config.dealerText.italic ? 'italic' : 'normal'}
                                >
                                    {config.dealerText.text}
                                </tspan>
                                {config.dealerPostcode && (
                                    <tspan
                                        dx={config.dealerSeparator || 5}
                                        fontFamily={config.dealerPostcodeConfig.fontFamily || config.dealerText.fontFamily}
                                        fontSize={config.dealerPostcodeConfig.fontSize}
                                        fontWeight={config.dealerPostcodeConfig.bold ? 'bold' : 'normal'}
                                        fontStyle={config.dealerPostcodeConfig.italic ? 'italic' : 'normal'}
                                    >
                                        {config.dealerPostcode}
                                    </tspan>
                                )}
                            </text>
                        )}
                        {/* Fill layer for dealer text */}
                        <text
                            x={footerCenterX}
                            y={getFooterY(config.dealerLocation, config.border, config.dealerText.fontSize, config.dealerVerticalOffset)}
                            textAnchor="middle"
                        >
                            {config.useDealerLogo && config.dealerLogo ? null : (
                                <>
                                    <tspan
                                        fill={config.dealerText.color}
                                        fontFamily={config.dealerText.fontFamily}
                                        fontSize={config.dealerText.fontSize}
                                        fontWeight={config.dealerText.bold ? 'bold' : 'normal'}
                                        fontStyle={config.dealerText.italic ? 'italic' : 'normal'}
                                        stroke={config.dealerText.bold ? config.dealerText.color : 'none'}
                                        strokeWidth={config.dealerText.bold ? 0.2 : 0}
                                    >
                                        {config.dealerText.text}
                                    </tspan>
                                    {config.dealerPostcode && (
                                        <tspan
                                            dx={config.dealerSeparator || 5}
                                            fill={config.dealerPostcodeConfig.color}
                                            fontFamily={config.dealerPostcodeConfig.fontFamily || config.dealerText.fontFamily}
                                            fontSize={config.dealerPostcodeConfig.fontSize}
                                            stroke={config.dealerPostcodeConfig.bold ? config.dealerPostcodeConfig.color : 'none'}
                                            strokeWidth={config.dealerPostcodeConfig.bold ? 0.2 : 0}
                                            fontWeight={config.dealerPostcodeConfig.bold ? 'bold' : 'normal'}
                                            fontStyle={config.dealerPostcodeConfig.italic ? 'italic' : 'normal'}
                                        >
                                            {config.dealerPostcode}
                                        </tspan>
                                    )}
                                </>
                            )}
                        </text>
                        {/* Outline effect layer for dealer text */}
                        {config.dealerText.outline && !config.useDealerLogo && (
                            <text
                                x={footerCenterX}
                                y={getFooterY(config.dealerLocation, config.border, config.dealerText.fontSize, config.dealerVerticalOffset)}
                                textAnchor="middle"
                                fill="none"
                                stroke={config.dealerText.color}
                                strokeWidth={0.5}
                            >
                                <tspan
                                    fontFamily={config.dealerText.fontFamily}
                                    fontSize={config.dealerText.fontSize}
                                    fontWeight={config.dealerText.bold ? 'bold' : 'normal'}
                                    fontStyle={config.dealerText.italic ? 'italic' : 'normal'}
                                >
                                    {config.dealerText.text}
                                </tspan>
                                {config.dealerPostcode && (
                                    <tspan
                                        dx={config.dealerSeparator || 5}
                                        fontFamily={config.dealerPostcodeConfig.fontFamily || config.dealerText.fontFamily}
                                        fontSize={config.dealerPostcodeConfig.fontSize}
                                        fontWeight={config.dealerPostcodeConfig.bold ? 'bold' : 'normal'}
                                        fontStyle={config.dealerPostcodeConfig.italic ? 'italic' : 'normal'}
                                    >
                                        {config.dealerPostcode}
                                    </tspan>
                                )}
                            </text>
                        )}
                    </g>
                )}

                {(showDealerCorner || showBSAU) && (
                    <g>
                        {/* Knockout stroke layer for corner/BSAU text - only show when ON_BORDER for visibility */}
                        {config.border && config.bsauLocation === 'ON_BORDER' && (
                            <text
                                x={dims.width - 8 - (config.bsauText.xOffset || 0)}
                                y={getFooterY(config.bsauLocation, config.border, config.bsauText.fontSize, config.bsauText.yOffset)}
                                textAnchor="end"
                                fill="none"
                                stroke={plateColor}
                                strokeWidth={5}
                                strokeLinejoin="round"
                                style={{ fontFamily: 'Arial', fontWeight: 'bold' }}
                            >
                                {showDealerCorner && (
                                    <>
                                        <tspan fontFamily={config.dealerText.fontFamily} fontSize={config.dealerText.fontSize}>{config.dealerText.text}</tspan>
                                        {config.dealerPostcode && (
                                            <tspan
                                                dx={5}
                                                fontSize={config.dealerPostcodeConfig.fontSize}
                                                fontFamily={config.dealerPostcodeConfig.fontFamily || config.dealerText.fontFamily}
                                            >
                                                {config.dealerPostcode}
                                            </tspan>
                                        )}
                                        {showBSAU && <tspan dx={config.dealerSeparator || 3} fontSize={3}>|</tspan>}
                                    </>
                                )}
                                {showBSAU && (
                                    <tspan
                                        dx={showDealerCorner ? (config.dealerSeparator || 3) : 0}
                                        fontFamily={config.bsauText.fontFamily}
                                        fontSize={config.bsauText.fontSize}
                                    >
                                        {config.bsauText.text}
                                    </tspan>
                                )}
                            </text>
                        )}
                        {/* Fill layer for corner/BSAU text */}
                        <text
                            x={dims.width - 8 - (config.bsauText.xOffset || 0)}
                            y={getFooterY(config.bsauLocation, config.border, config.bsauText.fontSize, config.bsauText.yOffset)}
                            textAnchor="end"
                            style={{ fontFamily: 'Arial', fontWeight: 'bold' }}
                        >
                            {showDealerCorner && (
                                <>
                                    <tspan fill={config.dealerText.color} fontFamily={config.dealerText.fontFamily} fontSize={config.dealerText.fontSize}>{config.dealerText.text}</tspan>
                                    {config.dealerPostcode && (
                                        <tspan
                                            dx={5}
                                            fill={config.dealerPostcodeConfig.color}
                                            fontSize={config.dealerPostcodeConfig.fontSize}
                                            fontFamily={config.dealerPostcodeConfig.fontFamily || config.dealerText.fontFamily}
                                        >
                                            {config.dealerPostcode}
                                        </tspan>
                                    )}
                                    {showBSAU && <tspan dx={config.dealerSeparator || 3} fill="#94a3b8" fontSize={3}>|</tspan>}
                                </>
                            )}
                            {showBSAU && (
                                <tspan
                                    dx={showDealerCorner ? (config.dealerSeparator || 3) : 0}
                                    fill={config.bsauText.color}
                                    fontFamily={config.bsauText.fontFamily}
                                    fontSize={config.bsauText.fontSize}
                                    fontWeight={config.bsauText.bold ? 'bold' : 'normal'}
                                    fontStyle={config.bsauText.italic ? 'italic' : 'normal'}
                                    stroke={config.bsauText.bold ? config.bsauText.color : 'none'}
                                    strokeWidth={config.bsauText.bold ? 0.15 : 0}
                                >
                                    {config.bsauText.text}
                                </tspan>
                            )}
                        </text>
                    </g>
                )}
            </g>
        </svg>
    );
  };

  // Calculate total height including lip - when lip is present, the plate container increases by lip height
  const lipHeight = config.lippedPlate ? config.lipHeight : 0;
  const totalHeight = dims.height + lipHeight;

  return (
    <div 
        id={id} 
        className={className} 
        style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: `${dims.width}${unit}`,
            height: `${totalHeight}${unit}`,
            // Floating positioning props for mobile (these take priority)
            ...(position && { position }),
            ...(top !== undefined && { top }),
            ...(left !== undefined && { left }),
            ...(right !== undefined && { right }),
            ...(bottom !== undefined && { bottom }),
            ...(transform && { transform }),
            ...(transformOrigin && { transformOrigin }),
            ...(transition && { transition }),
            ...(touchAction && { touchAction }),
            ...(zIndex !== undefined && { zIndex }),
            ...(pointerEvents && { pointerEvents }),
            // propStyle overrides come last so they can override everything
            ...propStyle
        }}
    >
        <div
            className="relative overflow-visible select-none box-border"
            style={{
                width: `${dims.width}${unit}`,
                height: `${totalHeight}${unit}`,
                backgroundColor: 'transparent',
                borderRadius: isShaped ? '0' : `${plateCornerRadius}${unit}`,
                position: 'relative',
                filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.1))'
            }}
        >
            <div 
                className="w-full relative"
                style={{
                    width: `${dims.width}${unit}`,
                    height: `${dims.height}${unit}`,
                    transform: `translate(${config.globalOffsetX}${unit}, ${-config.globalOffsetY}${unit})`
                }}
            >
                {/* Renders the Flag Graphic if enabled */}
                {renderBadge()}
                
                {config.useDealerLogo && config.dealerLogo && !config.supplierInCorner && !hasSlogan && (
                    <div 
                        style={{
                            position: 'absolute',
                            bottom: `${(config.borderMargin + config.borderThickness + 2) + (config.dealerVerticalOffset || 0)}${unit}`,
                            left: '0',
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 25,
                            pointerEvents: 'none'
                        }}
                    >
                        <img 
                            src={config.dealerLogo} 
                            alt="Dealer Logo" 
                            style={{
                                height: `${config.dealerLogoHeight || 8}${unit}`,
                                width: 'auto',
                                objectFit: 'contain'
                            }}
                        />
                    </div>
                )}

                {renderSVGContent()}

                {/* CROP LINES VISUALIZATION */}
                {showCropLines && cropLinesOffset > 0 && (
                    <svg
                        style={{
                            position: 'absolute',
                            top: `-${cropLinesOffset}${unit}`,
                            left: `-${cropLinesOffset}${unit}`,
                            width: `${dims.width + cropLinesOffset * 2}${unit}`,
                            height: `${dims.height + cropLinesOffset * 2}${unit}`,
                            overflow: 'visible',
                            pointerEvents: 'none',
                            zIndex: 50
                        }}
                        viewBox={`0 0 ${dims.width + cropLinesOffset * 2} ${dims.height + cropLinesOffset * 2}`}
                    >
                        {/* Offset area border (dashed) */}
                        <rect
                            x="0.5"
                            y="0.5"
                            width={dims.width + cropLinesOffset * 2 - 1}
                            height={dims.height + cropLinesOffset * 2 - 1}
                            fill="none"
                            stroke="#3b82f6"
                            strokeWidth="1"
                            strokeDasharray="4 2"
                            opacity="0.5"
                        />

                        {/* Top-left crop marks */}
                        <line x1="0" y1={cropLinesOffset} x2={cropLinesOffset * 0.8} y2={cropLinesOffset} stroke="#ef4444" strokeWidth="1" />
                        <line x1={cropLinesOffset} y1="0" x2={cropLinesOffset} y2={cropLinesOffset * 0.8} stroke="#ef4444" strokeWidth="1" />

                        {/* Top-right crop marks */}
                        <line x1={dims.width + cropLinesOffset * 2} y1={cropLinesOffset} x2={dims.width + cropLinesOffset * 1.2} y2={cropLinesOffset} stroke="#ef4444" strokeWidth="1" />
                        <line x1={dims.width + cropLinesOffset} y1="0" x2={dims.width + cropLinesOffset} y2={cropLinesOffset * 0.8} stroke="#ef4444" strokeWidth="1" />

                        {/* Bottom-left crop marks */}
                        <line x1="0" y1={dims.height + cropLinesOffset} x2={cropLinesOffset * 0.8} y2={dims.height + cropLinesOffset} stroke="#ef4444" strokeWidth="1" />
                        <line x1={cropLinesOffset} y1={dims.height + cropLinesOffset * 2} x2={cropLinesOffset} y2={dims.height + cropLinesOffset * 1.2} stroke="#ef4444" strokeWidth="1" />

                        {/* Bottom-right crop marks */}
                        <line x1={dims.width + cropLinesOffset * 2} y1={dims.height + cropLinesOffset} x2={dims.width + cropLinesOffset * 1.2} y2={dims.height + cropLinesOffset} stroke="#ef4444" strokeWidth="1" />
                        <line x1={dims.width + cropLinesOffset} y1={dims.height + cropLinesOffset * 2} x2={dims.width + cropLinesOffset} y2={dims.height + cropLinesOffset * 1.2} stroke="#ef4444" strokeWidth="1" />
                    </svg>
                )}
            </div>

            {/* LIPPED PLATE ATTACHMENT - positioned at bottom of expanded container */}
            {config.lippedPlate && (
                <div
                    style={{
                        position: 'absolute',
                        bottom: '0',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: `${config.lipWidth || dims.width}${unit}`,
                        height: `${config.lipHeight || 15}${unit}`,
                        zIndex: 10,
                        overflow: 'hidden',
                        backgroundColor: config.lipImage ? 'transparent' : (config.lipBackgroundColor || plateColor) // Fill with custom color or plate color by default
                    }}
                >
                    {/* Background Image */}
                    {config.lipImage && (
                        <img
                            src={config.lipImage}
                            alt="Lip Background"
                            style={{
                                position: 'absolute',
                                top: '0',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                height: '100%',
                                maxWidth: '100%',
                                objectFit: 'contain',
                                pointerEvents: 'none'
                            }}
                        />
                    )}

                    {/* Lip Images - positioned individually */}
                    {config.lipImages && config.lipImages.map((lipImg: LipImageItem) => (
                        <img
                            key={lipImg.id}
                            src={lipImg.image}
                            alt="Lip Image"
                            style={{
                                position: 'absolute',
                                left: `calc(50% + ${lipImg.xOffset}${unit})`,
                                top: `calc(50% + ${lipImg.yOffset}${unit})`,
                                transform: 'translate(-50%, -50%)',
                                width: `${lipImg.width}${unit}`,
                                height: `${lipImg.height}${unit}`,
                                objectFit: 'contain',
                                pointerEvents: 'none'
                            }}
                        />
                    ))}

                    {/* Lip Text Boxes - Multiple */}
                    {config.lipTexts && config.lipTexts.map((textItem: LipTextItem) => (
                        textItem.enabled && textItem.text && (
                            <div
                                key={textItem.id}
                                style={{
                                    position: 'absolute',
                                    left: `calc(50% + ${textItem.xOffset || 0}${unit})`,
                                    top: `calc(50% + ${textItem.yOffset || 0}${unit})`,
                                    transform: 'translate(-50%, -50%)',
                                    fontFamily: textItem.fontFamily || 'Arial',
                                    fontSize: `${textItem.fontSize || 4}${unit}`,
                                    color: textItem.color || '#000000',
                                    fontWeight: textItem.bold ? 'bold' : 'normal',
                                    fontStyle: textItem.italic ? 'italic' : 'normal',
                                    whiteSpace: 'nowrap',
                                    pointerEvents: 'none'
                                }}
                            >
                                {textItem.text}
                            </div>
                        )
                    ))}
                    {/* Legacy single lip text (for backward compatibility) */}
                    {config.lipText && config.lipText.enabled && config.lipText.text && !config.lipTexts && (
                        <div
                            style={{
                                position: 'absolute',
                                left: `calc(50% + ${config.lipText.xOffset || 0}${unit})`,
                                top: `calc(50% + ${config.lipText.yOffset || 0}${unit})`,
                                transform: 'translate(-50%, -50%)',
                                fontFamily: config.lipText.fontFamily || 'Arial',
                                fontSize: `${config.lipText.fontSize || 4}${unit}`,
                                color: config.lipText.color || '#000000',
                                fontWeight: config.lipText.bold ? 'bold' : 'normal',
                                fontStyle: config.lipText.italic ? 'italic' : 'normal',
                                whiteSpace: 'nowrap',
                                pointerEvents: 'none'
                            }}
                        >
                            {config.lipText.text}
                        </div>
                    )}
                </div>
            )}
        </div>
    </div>
  );
};
